class DartyHeroSlider extends HTMLElement {
  constructor() {
    super();

    this.swiperId = this.id;
    this.dataSelector = this.getAttribute("data-selector");
    this.dataTargetSelector = this.getAttribute("data-selector");
    this.swiperDiv = this.querySelector(`.${this.dataSelector}`);
    this.dataSliderContent = this.querySelector(".hero-slider-content-wrapper");
    this.dataAutoplayDelay = this.dataset.autoplayDelay ?? 4000;
    
    // Cache for performance
    this.swiperInstance = null;
    this.isInitializing = false;
    this.isInitialized = false;
    this.observer = null;
    
    // Bind methods for better performance
    this.initSwiperInstance = this.initSwiperInstance.bind(this);
    this.handleIntersection = this.handleIntersection.bind(this);
    this.handleSlideChange = this.handleSlideChange.bind(this);
  }

  connectedCallback() {
    // Always initialize hero sliders immediately for better UX
    this.initializeImmediately();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  initializeImmediately() {
    if (this.isInitialized || this.isInitializing) return;
    
    if (document.readyState === "complete" || document.readyState === "interactive") {
      this.initSwiperInstance();
    } else {
      // Use more efficient event listener
      const onReady = () => {
        this.initSwiperInstance();
        document.removeEventListener("DOMContentLoaded", onReady);
      };
      document.addEventListener("DOMContentLoaded", onReady, { once: true, passive: true });
    }
  }

  handleIntersection(entries) {
    const entry = entries[0];
    if (entry.isIntersecting && !this.isInitialized && !this.isInitializing) {
      console.debug("Creating instance for hero swiper...");
      this.initSwiperInstance();
      
      // Stop observing once initialized
      if (this.observer) {
        this.observer.unobserve(entry.target);
        this.observer.disconnect();
        this.observer = null;
      }
    }
  }

  initSwiperInstance() {
    if (this.isInitialized || this.isInitializing) return;
    
    this.isInitializing = true;
    
    const selectorString = `.${this.dataSelector}`;
    const swiperElement = document.querySelector(selectorString);
    
    if (!swiperElement) {
      console.warn(`Hero Swiper element not found: ${selectorString}`);
      this.isInitializing = false;
      return;
    }

    // Cleanup any existing instance
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
    }

    // Use requestAnimationFrame for smoother initialization
    requestAnimationFrame(() => {
      try {
        this.swiperInstance = new Swiper(selectorString, this.getSwiperOptions());
        this.isInitialized = true;
        this.isInitializing = false;
      } catch (error) {
        console.error("Failed to initialize hero swiper:", error);
        this.isInitializing = false;
      }
    });
  }

  cleanup() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    
    if (this.swiperInstance) {
      this.swiperInstance.destroy(true, true);
      this.swiperInstance = null;
    }
    
    this.isInitialized = false;
    this.isInitializing = false;
  }

  getSwiperOptions = () => {
    if (!this._swiperOptions) {
      this._swiperOptions = {
        pagination: {
          el: ".swiper-pagination",
          clickable: true,
        },
        navigation: {
          nextEl: ".swiper-button-next",
          prevEl: ".swiper-button-prev",
        },
        centeredSlides: true,
        autoplay: {
          delay: parseInt(this.dataAutoplayDelay, 10),
          disableOnInteraction: false,
          pauseOnMouseEnter: true, // Better UX
        },
        mousewheel: { 
          forceToAxis: true,
          sensitivity: 1, // Better control
        },
        grabCursor: true,
        speed: 300, // Smoother transitions
        updateOnWindowResize: true,
        observer: false,
        observeParents: false,
        runCallbacksOnInit: false, // Better performance
        on: {
          slideChange: this.handleSlideChange,
          init: (swiper) => {
            // Ensure first slide is active
            if (this.dataSliderContent && this.dataSliderContent.children.length > 0) {
              const firstSlide = this.dataSliderContent.children.item(0);
              if (firstSlide) {
                firstSlide.setAttribute("data-active", "true");
              }
            }
          },
        },
      };
    }
    return this._swiperOptions;
  };

  handleSlideChange = (slide) => {
    // Performance optimization: use cached references and batch DOM updates
    if (!this.dataSliderContent) return;

    const children = this.dataSliderContent.children;
    if (!children || children.length === 0) return;

    // Batch DOM updates for better performance
    requestAnimationFrame(() => {
      const previousContentSlide = children.item(slide.previousIndex);
      const activeContentSlide = children.item(slide.activeIndex);

      if (previousContentSlide) {
        previousContentSlide.setAttribute("data-active", "false");
      }
      if (activeContentSlide) {
        activeContentSlide.setAttribute("data-active", "true");
      }
    });
  };
}

// Enhanced event handling for Shopify theme editor
document.addEventListener("shopify:section:load", (event) => {
  const heroSliders = event.target.querySelectorAll('darty-hero-slider');
  heroSliders.forEach(slider => {
    if (slider.swiperInstance) {
      slider.cleanup();
      slider.connectedCallback();
    }
  });
});

document.addEventListener("shopify:section:unload", (event) => {
  const heroSliders = event.target.querySelectorAll('darty-hero-slider');
  heroSliders.forEach(slider => {
    slider.cleanup();
  });
});

customElements.define("darty-hero-slider", DartyHeroSlider);